import { useEffect, useRef, useState } from "react";
import {
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import {
  Building2,
  Calendar as CalendarIcon,
  Check,
  CheckCircle,
  ChevronRight,
  Clock,
  Copy,
  DollarSign,
  FileText,
  History,
  Mail,
  Printer,
  Scissors,
  User,
  X,
} from "lucide-react";
import axiosClient from "../../api/axiosClient";
import { ComingSoonTag } from "../../utils/comingSoon";

import "./AdminPayrollPage.css";

const { Text } = Typography;
const { RangePicker } = DatePicker;

// ── Types ─────────────────────────────────────────────────────────────────────

type PayStructure = "salary" | "commission" | "both";
type PayMethod = "direct-deposit" | "check";

type StaffPayrollInfo = {
  id: string;
  name: string;
  role: string;
  avatar: string;
  payStructure: PayStructure;
  salary: number;
  serviceCommissionRate: number;
  productCommissionRate: number;
  bankInfo: {
    bankName: string;
    routingNumber: string;
    accountNumber: string;
    accountType: "checking" | "savings";
  };
};

type WorkDay = {
  date: string;
  dayName: string;
  timeIn: string;
  timeOut: string;
  hoursWorked: number;
  services: { name: string; qty: number; rate: number; gross: number }[];
  products: { name: string; qty: number; price: number; gross: number }[];
};

type PayoutSummary = {
  workDays: WorkDay[];
  totalHours: number;
  totalServicesGross: number;
  totalProductsGross: number;
  serviceCommission: number;
  productCommission: number;
  basePay: number;
  totalPayout: number;
  staffMember: StaffPayrollInfo;
  fromDate: string;
  toDate: string;
};

type SalonBankInfo = {
  bankName: string;
  routingNumber: string;
  accountNumber: string;
  salonName: string;
  salonAddress: string;
  salonCity: string;
  salonState: string;
  salonZip: string;
  startingCheckNumber: number;
};

type PayoutRecord = {
  id: string;
  processedDate: string;
  fromDate: string;
  toDate: string;
  fromDateISO: string;
  toDateISO: string;
  amount: number;
  method: PayMethod;
  checkNumber?: number;
  daysWorked: number;
  hoursWorked: number;
  status: "completed" | "pending";
};


// Fetches the staff member's real completed bookings for the period and computes pay
// from them — replaces what used to be a seeded-random fake generator. There is no
// product-sale tracking anywhere in the app yet, so totalProductsGross/productCommission
// are always 0 here rather than fabricated; the Product Commission row simply won't
// render (see the `> 0` guards below) until that data source exists.
async function fetchPayoutData(staff: StaffPayrollInfo, fromISO: string, toISO: string): Promise<PayoutSummary> {
  const res = await axiosClient.get("/payroll/summary", { params: { staffId: staff.id, from: fromISO, to: toISO } });
  const data: {
    days: { date: string; day_name: string; time_in: string; time_out: string; hours_worked: number; services: { name: string; qty: number; gross: number }[] }[];
    total_hours: number;
    total_services_gross: number;
  } = res.data;

  const days: WorkDay[] = data.days.map((d) => ({
    date: d.date,
    dayName: d.day_name,
    timeIn: d.time_in,
    timeOut: d.time_out,
    hoursWorked: Number(d.hours_worked.toFixed(2)),
    services: d.services.map((s) => ({ name: s.name, qty: s.qty, rate: s.gross / s.qty, gross: s.gross })),
    products: [],
  }));

  const totalHours = Number(data.total_hours.toFixed(2));
  const totalServicesGross = data.total_services_gross;
  const totalProductsGross = 0;
  // staff.salary is a monthly figure — prorate it across the actual selected pay period
  // (which may be any length, not just biweekly) rather than assuming a fixed 26 periods/year.
  const periodDays = (new Date(toISO).getTime() - new Date(fromISO).getTime()) / 86400000 + 1;
  const basePay = staff.payStructure !== "commission" ? (staff.salary * periodDays / 30) : 0;
  const serviceCommission = staff.payStructure !== "salary" ? totalServicesGross * (staff.serviceCommissionRate / 100) : 0;
  const productCommission = 0;

  return {
    workDays: days, totalHours, totalServicesGross, totalProductsGross,
    serviceCommission, productCommission, basePay,
    totalPayout: basePay + serviceCommission + productCommission,
    staffMember: staff, fromDate: fromISO, toDate: toISO,
  };
}

const DEFAULT_STAFF: StaffPayrollInfo[] = [
  { id: "s1", name: "Marcus Williams", role: "Owner", avatar: "MW", payStructure: "salary", salary: 8000, serviceCommissionRate: 0, productCommissionRate: 0, bankInfo: { bankName: "Chase Bank", routingNumber: "021000021", accountNumber: "****4521", accountType: "checking" } },
  { id: "s2", name: "Natalie Anderson", role: "Service Provider", avatar: "NA", payStructure: "commission", salary: 0, serviceCommissionRate: 45, productCommissionRate: 10, bankInfo: { bankName: "Bank of America", routingNumber: "026009593", accountNumber: "****8832", accountType: "checking" } },
  { id: "s3", name: "Michael Chen", role: "Service Provider", avatar: "MC", payStructure: "commission", salary: 0, serviceCommissionRate: 50, productCommissionRate: 10, bankInfo: { bankName: "Wells Fargo", routingNumber: "121042882", accountNumber: "****3317", accountType: "savings" } },
  { id: "s4", name: "Chelsea Martinez", role: "Service Provider", avatar: "CM", payStructure: "both", salary: 1200, serviceCommissionRate: 35, productCommissionRate: 10, bankInfo: { bankName: "TD Bank", routingNumber: "031101266", accountNumber: "****9904", accountType: "checking" } },
  { id: "s5", name: "Jordan Taylor", role: "Front Desk", avatar: "JT", payStructure: "salary", salary: 3200, serviceCommissionRate: 0, productCommissionRate: 0, bankInfo: { bankName: "Citibank", routingNumber: "021000089", accountNumber: "****6678", accountType: "checking" } },
];

const DEFAULT_SALON_BANK: SalonBankInfo = {
  bankName: "Chase Bank", routingNumber: "021000021", accountNumber: "****7754",
  salonName: "Hairplane Salon", salonAddress: "1234 Fifth Avenue",
  salonCity: "New York", salonState: "NY", salonZip: "10001",
  startingCheckNumber: 1042,
};

const AVATAR_COLORS = ["#3b82f6", "#8b5cf6", "#10b981", "#ec4899", "#f59e0b"];
function avatarColor(id: string) { return AVATAR_COLORS[id.charCodeAt(1) % AVATAR_COLORS.length]; }

function payStructureLabel(s: StaffPayrollInfo) {
  if (s.payStructure === "salary") return `Salary $${s.salary.toLocaleString()}/mo`;
  if (s.payStructure === "commission") return `${s.serviceCommissionRate}% commission`;
  return `Base + ${s.serviceCommissionRate}% commission`;
}

// ── Number to words (for check) ───────────────────────────────────────────────

const ONES = ["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten","Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"];
const TENS = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];

function convertHundreds(n: number): string {
  if (n === 0) return "";
  if (n < 20) return ONES[n];
  if (n < 100) return TENS[Math.floor(n / 10)] + (n % 10 ? "-" + ONES[n % 10] : "");
  return ONES[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + convertHundreds(n % 100) : "");
}

function numberToWords(amount: number): string {
  const dollars = Math.floor(amount);
  const cents = Math.round((amount - dollars) * 100);
  let result = "";
  if (dollars >= 1000000) result += convertHundreds(Math.floor(dollars / 1000000)) + " Million ";
  if (dollars >= 1000) result += convertHundreds(Math.floor((dollars % 1000000) / 1000)) + " Thousand ";
  result += convertHundreds(dollars % 1000);
  result = result.trim() || "Zero";
  return `${result} and ${cents.toString().padStart(2, "0")}/100`;
}

// ── Check Writer Modal ────────────────────────────────────────────────────────

function CheckWriterModal({
  amount, payToName, salonBank, checkNumber, memo, onMemoChange, onClose,
}: {
  amount: number; payToName: string; salonBank: SalonBankInfo;
  checkNumber: number; memo: string;
  onMemoChange: (v: string) => void; onClose: () => void;
}) {
  const checkRef = useRef<HTMLDivElement>(null);
  const dateStr = new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });

  function handlePrint() {
    const win = window.open("", "_blank", "width=900,height=500");
    if (!win) return;
    win.document.write(`<html><head><title>Check #${checkNumber}</title>
      <style>
        body{margin:0;padding:20px;font-family:Arial,sans-serif}
        .check{border:2px solid #333;border-radius:8px;padding:24px;max-width:780px;margin:0 auto;background:#fffff8}
        .row{display:flex;align-items:center;margin-bottom:12px}
        .label{font-size:12px;font-weight:bold;margin-right:8px;white-space:nowrap}
        .line{border-bottom:1px solid #333;flex:1;padding:2px 6px;font-size:14px}
        .amt{border:2px solid #333;padding:4px 12px;font-weight:bold;margin-left:12px;white-space:nowrap}
        .micr{font-family:monospace;font-size:13px;text-align:center;border-top:1px dashed #aaa;padding-top:8px;margin-top:16px;letter-spacing:3px}
      </style></head><body>
      ${checkRef.current?.innerHTML ?? ""}
    </body></html>`);
    win.document.close();
    setTimeout(() => { win.print(); win.close(); }, 400);
  }

  return (
    <Modal
      open
      title={<span style={{ display: "flex", alignItems: "center", gap: 8 }}><Printer size={18} color="#1677ff" /> Check Writer — #{checkNumber}</span>}
      onCancel={onClose}
      width={720}
      footer={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" icon={<Printer size={16} />} onClick={handlePrint}>Print Check</Button>
        </Space>
      }
    >
      {/* The rendered check */}
      <div ref={checkRef} className="check-outer">
        <div className="check-header">
          <div>
            <div className="check-salon-name">{salonBank.salonName}</div>
            <div className="check-salon-addr">{salonBank.salonAddress}</div>
            <div className="check-salon-addr">{salonBank.salonCity}, {salonBank.salonState} {salonBank.salonZip}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 11, color: "#6b7280" }}>Check No.</div>
            <div style={{ fontWeight: 700, fontFamily: "monospace", fontSize: 14 }}>{String(checkNumber).padStart(4, "0")}</div>
          </div>
        </div>

        <div className="check-date-row">
          <span className="check-field-label">DATE</span>
          <div className="check-underline" style={{ minWidth: 120, textAlign: "center" }}>{dateStr}</div>
        </div>

        <div className="check-pay-row">
          <span className="check-field-label">PAY TO THE ORDER OF</span>
          <div className="check-underline" style={{ flex: 1 }}>{payToName}</div>
          <div className="check-amt-box">$ {amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}</div>
        </div>

        <div className="check-words-row">
          <div className="check-underline" style={{ flex: 1, fontStyle: "italic" }}>{numberToWords(amount)}</div>
          <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 8 }}>DOLLARS</span>
        </div>

        <div className="check-bottom">
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280" }}>BANK</div>
            <div style={{ fontSize: 13 }}>{salonBank.bankName}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ borderTop: "1px solid #aaa", width: 160, marginTop: 32, paddingTop: 4, fontSize: 11, color: "#9ca3af" }}>Authorized Signature</div>
          </div>
        </div>

        <div className="check-memo-row">
          <span className="check-field-label">MEMO</span>
          <div className="check-underline" style={{ flex: 1 }}>{memo}</div>
        </div>

        <div className="check-micr">
          ⑆{salonBank.routingNumber}⑆ ⑈{salonBank.accountNumber}⑈ {String(checkNumber).padStart(4, "0")}⑈
        </div>
      </div>

      {/* Memo editor */}
      <div style={{ marginTop: 16 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>Memo (editable)</Text>
        <Input
          value={memo}
          onChange={(e) => onMemoChange(e.target.value)}
          placeholder="e.g. Payroll May 16 – May 31"
          style={{ marginTop: 4 }}
        />
      </div>
    </Modal>
  );
}

// ── Bank Info Edit Modal ──────────────────────────────────────────────────────

function BankInfoModal({
  staffName, bankInfo, onSave, onClose,
}: {
  staffName: string;
  bankInfo: StaffPayrollInfo["bankInfo"];
  onSave: (info: StaffPayrollInfo["bankInfo"]) => void;
  onClose: () => void;
}) {
  const [form] = Form.useForm();
  async function handleOk() {
    const vals = await form.validateFields();
    onSave(vals as StaffPayrollInfo["bankInfo"]);
  }
  return (
    <Modal open title={`Bank Info — ${staffName}`} onCancel={onClose} onOk={handleOk} okText="Save" width={420}>
      <Form form={form} layout="vertical" initialValues={bankInfo} style={{ marginTop: 16 }}>
        <Form.Item name="bankName" label="Bank Name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Form.Item name="routingNumber" label="Routing Number" rules={[{ required: true }]}>
            <Input style={{ fontFamily: "monospace" }} maxLength={9} />
          </Form.Item>
          <Form.Item name="accountNumber" label="Account Number" rules={[{ required: true }]}>
            <Input style={{ fontFamily: "monospace" }} />
          </Form.Item>
        </div>
        <Form.Item name="accountType" label="Account Type">
          <Select options={[{ value: "checking", label: "Checking" }, { value: "savings", label: "Savings" }]} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── Payout History Modal ──────────────────────────────────────────────────────

function PayoutHistoryModal({
  staff, history, onClose, onViewDetail,
}: {
  staff: StaffPayrollInfo;
  history: PayoutRecord[];
  onClose: () => void;
  onViewDetail: (rec: PayoutRecord) => void;
}) {
  const [messageApi, contextHolder] = message.useMessage();
  const totalPaid = history.reduce((s, r) => s + r.amount, 0);

  function summaryText() {
    const lines = history.map(
      (r) => `${r.processedDate}: ${r.fromDate} – ${r.toDate} — $${r.amount.toFixed(2)} (${r.method === "direct-deposit" ? "Direct Deposit" : `Check #${r.checkNumber}`})`
    );
    return `Payout history for ${staff.name}\n\n${lines.join("\n")}\n\nTotal Paid (all time): $${totalPaid.toFixed(2)}`;
  }

  function handlePrint() {
    window.print();
  }

  function handleEmail() {
    window.location.href = `mailto:?subject=${encodeURIComponent(`Payout History — ${staff.name}`)}&body=${encodeURIComponent(summaryText())}`;
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(summaryText());
      messageApi.success("Payout history copied to clipboard");
    } catch {
      messageApi.error("Failed to copy");
    }
  }

  return (
    <Modal open title={`All Payout History — ${staff.name}`} onCancel={onClose} footer={<Button onClick={onClose}>Close</Button>} width={680}>
      {contextHolder}
      <Space style={{ marginBottom: 12 }}>
        <Button size="small" icon={<Printer size={14} />} onClick={handlePrint}>Print</Button>
        <Button size="small" icon={<Mail size={14} />} onClick={handleEmail}>Email</Button>
        <Button size="small" icon={<Copy size={14} />} onClick={handleCopy}>Copy Details</Button>
      </Space>
      <table className="payroll-hist-table">
        <thead>
          <tr>
            <th>Processed</th><th>Pay Period</th><th>Method</th>
            <th className="text-right">Days/Hours</th>
            <th className="text-right">Amount</th><th></th>
          </tr>
        </thead>
        <tbody>
          {history.map((rec) => (
            <tr key={rec.id}>
              <td>{rec.processedDate}</td>
              <td style={{ color: "#6b7280" }}>{rec.fromDate} – {rec.toDate}</td>
              <td>
                {rec.method === "direct-deposit"
                  ? <Tag color="blue" icon={<Building2 size={10} />}> Direct Deposit</Tag>
                  : <Tag color="gold" icon={<Printer size={10} />}> Check #{rec.checkNumber}</Tag>}
              </td>
              <td className="text-right" style={{ color: "#6b7280" }}>{rec.daysWorked}d / {rec.hoursWorked}h</td>
              <td className="text-right" style={{ fontWeight: 600 }}>${rec.amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
              <td><Button size="small" type="link" onClick={() => onViewDetail(rec)}>View</Button></td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4} style={{ fontWeight: 600 }}>{history.length} payouts on record</td>
            <td className="text-right" style={{ fontWeight: 700 }}>${totalPaid.toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
            <td></td>
          </tr>
          <tr>
            <td colSpan={4} style={{ color: "#6b7280", fontSize: 12 }}>Total Paid (all time)</td>
            <td></td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </Modal>
  );
}

// ── Payout Detail Modal ───────────────────────────────────────────────────────

function PayoutDetailModal({
  record, summary, onClose,
}: {
  record: PayoutRecord;
  summary: PayoutSummary;
  onClose: () => void;
}) {
  const staff = summary.staffMember;

  const combinedServices: Record<string, { qty: number; rate: number; gross: number }> = {};
  summary.workDays.forEach((d) =>
    d.services.forEach((svc) => {
      if (!combinedServices[svc.name]) combinedServices[svc.name] = { qty: 0, rate: svc.rate, gross: 0 };
      combinedServices[svc.name].qty += svc.qty;
      combinedServices[svc.name].gross += svc.gross;
    })
  );

  return (
    <Modal
      open
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: avatarColor(staff.id), display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 13 }}>
            {staff.avatar}
          </div>
          <div>
            <div style={{ fontWeight: 700 }}>{staff.name}</div>
            <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 400 }}>{record.fromDate} – {record.toDate}</div>
          </div>
        </div>
      }
      onCancel={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
      width={700}
    >
      {/* Stats */}
      <div className="payout-detail-stats">
        {[
          { label: "Days Worked", value: summary.workDays.length },
          { label: "Total Hours", value: summary.totalHours + "h" },
          { label: "Services Gross", value: "$" + summary.totalServicesGross.toFixed(2) },
          { label: "Products Gross", value: "$" + summary.totalProductsGross.toFixed(2) },
        ].map((s) => (
          <div key={s.label} className="payout-detail-stat">
            <div className="payout-detail-stat-label">{s.label}</div>
            <div className="payout-detail-stat-value">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Work Log */}
      <div className="payout-detail-section">
        <div className="payout-detail-section-title"><Clock size={14} /> Work Log</div>
        <table className="payroll-inner-table">
          <thead><tr><th>Date</th><th>In</th><th>Out</th><th className="text-right">Hours</th></tr></thead>
          <tbody>
            {summary.workDays.slice(0, 10).map((d, i) => (
              <tr key={i}><td>{d.dayName}, {d.date}</td><td>{d.timeIn}</td><td>{d.timeOut}</td><td className="text-right">{d.hoursWorked}h</td></tr>
            ))}
            {summary.workDays.length > 10 && <tr><td colSpan={4} className="more-row">+{summary.workDays.length - 10} more days</td></tr>}
          </tbody>
          <tfoot><tr><td colSpan={3} style={{ fontWeight: 600 }}>Total</td><td className="text-right" style={{ fontWeight: 700 }}>{summary.totalHours}h</td></tr></tfoot>
        </table>
      </div>

      {/* Services */}
      <div className="payout-detail-section">
        <div className="payout-detail-section-title"><Scissors size={14} /> Services Performed</div>
        <table className="payroll-inner-table">
          <thead><tr><th>Service</th><th className="text-right">Qty</th><th className="text-right">Rate</th><th className="text-right">Gross</th><th className="text-right">Comm%</th><th className="text-right">Earned</th></tr></thead>
          <tbody>
            {Object.entries(combinedServices).map(([name, data]) => (
              <tr key={name}>
                <td>{name}</td>
                <td className="text-right">{data.qty}</td>
                <td className="text-right">${data.rate}</td>
                <td className="text-right">${data.gross.toFixed(2)}</td>
                <td className="text-right">{staff.serviceCommissionRate}%</td>
                <td className="text-right" style={{ fontWeight: 600 }}>${(data.gross * staff.serviceCommissionRate / 100).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3} style={{ fontWeight: 600 }}>Total</td>
              <td className="text-right">${summary.totalServicesGross.toFixed(2)}</td>
              <td></td>
              <td className="text-right" style={{ fontWeight: 700, color: "#1677ff" }}>${summary.serviceCommission.toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Payout breakdown */}
      <div className="payout-breakdown-card">
        <div className="payout-breakdown-label">Payout Breakdown</div>
        {summary.basePay > 0 && (
          <div className="payout-breakdown-row"><span>Base Pay (prorated)</span><span>${summary.basePay.toFixed(2)}</span></div>
        )}
        {summary.serviceCommission > 0 && (
          <div className="payout-breakdown-row"><span>Service Commission ({staff.serviceCommissionRate}%)</span><span>${summary.serviceCommission.toFixed(2)}</span></div>
        )}
        {summary.productCommission > 0 && (
          <div className="payout-breakdown-row"><span>Product Commission ({staff.productCommissionRate}%)</span><span>${summary.productCommission.toFixed(2)}</span></div>
        )}
        <div className="payout-breakdown-total">
          <span>Total Paid</span>
          <span>${record.amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
        </div>
      </div>
    </Modal>
  );
}


// ── Main Page ─────────────────────────────────────────────────────────────────

type PayrollStep = 1 | 2 | 3 | 4;

export default function AdminPayrollPage() {
  const [activeTab, setActiveTab] = useState<"payroll" | "settings">("payroll");
  const [settingsTab, setSettingsTab] = useState<"staff" | "salon">("staff");

  // Payroll flow
  const [staffList, setStaffList] = useState<StaffPayrollInfo[]>([]);
  const [selectedStaff, setSelectedStaff] = useState<StaffPayrollInfo | null>(null);
  const [payrollStep, setPayrollStep] = useState<PayrollStep>(1);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [payoutSummary, setPayoutSummary] = useState<PayoutSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [showCheckWriter, setShowCheckWriter] = useState(false);
  const [checkMemo, setCheckMemo] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PayMethod | null>(null);
  const [showDepositConfirm, setShowDepositConfirm] = useState(false);
  const [showHistoryAll, setShowHistoryAll] = useState(false);
  const [historyDetail, setHistoryDetail] = useState<{ record: PayoutRecord; summary: PayoutSummary } | null>(null);
  const [history, setHistory] = useState<PayoutRecord[]>([]);

  // Payment settings
  const [salonBank, setSalonBank] = useState(DEFAULT_SALON_BANK);
  const [editBankFor, setEditBankFor] = useState<string | null>(null);
  const [editSalonBank, setEditSalonBank] = useState(false);
  const [salonBankForm, setSalonBankForm] = useState(DEFAULT_SALON_BANK);

  const summaryRef = useRef<HTMLDivElement>(null);
  const paymentRef = useRef<HTMLDivElement>(null);

  // Fetch staff + compensation + bank info + salon bank on mount
  useEffect(() => {
    async function loadData() {
      try {
        const [staffRes, compensationRes, bankInfoRes, salonBankRes] = await Promise.allSettled([
          axiosClient.get("/staff"),
          axiosClient.get("/payroll/compensation"),
          axiosClient.get("/payroll/bank-info"),
          axiosClient.get("/payroll/salon-bank"),
        ]);

        const roleNameLabel: Record<string, string> = {
          ADMIN: "Owner",
          BARBER: "Service Provider",
          FRONT_DESK: "Front Desk",
        };
        const barbers: { user_id: string; name: string; role_name: string }[] =
          staffRes.status === "fulfilled" ? (staffRes.value.data ?? []) : DEFAULT_STAFF.map(s => ({ user_id: s.id, name: s.name, role_name: s.role }));

        type CompRow = {
          staff_id: string;
          service_commission_enabled: boolean;
          service_commission_basic_rate: number;
          product_commission_enabled: boolean;
          product_commission_basic_rate: number;
          hourly_enabled: boolean;
          hourly_rate: number;
          salary_enabled: boolean;
          monthly_salary: number;
        };
        const compensation: CompRow[] =
          compensationRes.status === "fulfilled" ? (compensationRes.value.data ?? []) : [];

        type BankRow = { staff_id: string; bank_name: string; routing_number: string; account_number: string; account_type: string };
        const bankRows: BankRow[] =
          bankInfoRes.status === "fulfilled" ? (bankInfoRes.value.data ?? []) : [];

        const compMap = Object.fromEntries(compensation.map((c) => [c.staff_id, c]));
        const bankMap = Object.fromEntries(bankRows.map((b) => [b.staff_id, b]));

        const merged: StaffPayrollInfo[] = barbers.map((b) => {
          const comp = compMap[b.user_id];
          const bank = bankMap[b.user_id];
          const nameParts = b.name.trim().split(" ");
          const avatar = nameParts.map((p: string) => p[0] ?? "").join("").slice(0, 2).toUpperCase();
          let payStructure: PayStructure = "commission";
          let salary = 0;
          let serviceCommissionRate = 0;
          let productCommissionRate = 10;
          if (comp) {
            const hasCommission = comp.service_commission_enabled || comp.product_commission_enabled;
            if (comp.salary_enabled && hasCommission) {
              payStructure = "both";
            } else if (comp.salary_enabled) {
              payStructure = "salary";
            } else {
              payStructure = "commission";
            }
            salary = comp.salary_enabled ? comp.monthly_salary : 0;
            serviceCommissionRate = comp.service_commission_enabled ? comp.service_commission_basic_rate : 0;
            productCommissionRate = comp.product_commission_enabled ? comp.product_commission_basic_rate : 10;
          }
          return {
            id: b.user_id,
            name: b.name,
            role: roleNameLabel[b.role_name] ?? b.role_name,
            avatar,
            payStructure,
            salary,
            serviceCommissionRate,
            productCommissionRate,
            bankInfo: bank
              ? { bankName: bank.bank_name, routingNumber: bank.routing_number, accountNumber: bank.account_number, accountType: bank.account_type as "checking" | "savings" }
              : { bankName: "", routingNumber: "", accountNumber: "****0000", accountType: "checking" },
          };
        });

        setStaffList(merged.length > 0 ? merged : DEFAULT_STAFF);

        if (salonBankRes.status === "fulfilled" && salonBankRes.value.data) {
          const sb = salonBankRes.value.data;
          setSalonBank((prev) => ({
            ...prev,
            bankName: sb.bank_name || prev.bankName,
            routingNumber: sb.routing_number || prev.routingNumber,
            accountNumber: sb.account_number || prev.accountNumber,
            salonName: sb.salon_name || prev.salonName,
            salonAddress: sb.salon_address || prev.salonAddress,
            salonCity: sb.salon_city || prev.salonCity,
            salonState: sb.salon_state || prev.salonState,
            salonZip: sb.salon_zip || prev.salonZip,
            startingCheckNumber: sb.starting_check_number || prev.startingCheckNumber,
          }));
        }
      } catch {
        setStaffList(DEFAULT_STAFF);
      }
    }
    loadData();
  }, []);

  // Fetch payout history when a staff member is selected
  useEffect(() => {
    if (!selectedStaff) { setHistory([]); return; }
    axiosClient.get("/payroll/history").then((res) => {
      const all: {
        payout_id: string; staff_id: string; period_start: string; period_end: string;
        net_pay: number; payment_method: string; paid_at: string; notes: string;
      }[] = res.data ?? [];
      const staffHistory: PayoutRecord[] = all
        .filter((r) => r.staff_id === selectedStaff.id)
        .map((r, i) => ({
          id: r.payout_id,
          processedDate: new Date(r.paid_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
          fromDate: new Date(r.period_start + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
          toDate: new Date(r.period_end + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
          fromDateISO: r.period_start,
          toDateISO: r.period_end,
          amount: r.net_pay,
          method: r.payment_method === "check" ? "check" : "direct-deposit",
          checkNumber: r.payment_method === "check" ? salonBank.startingCheckNumber + i : undefined,
          daysWorked: 0,
          hoursWorked: 0,
          status: "completed" as const,
        }));
      setHistory(staffHistory);
    }).catch(() => {
      setHistory([]);
    });
  }, [selectedStaff]);

  // Returns whether the payout was saved, so callers only show the success
  // screen when it really was.
  async function recordPayout(method: PayMethod): Promise<boolean> {
    if (!selectedStaff || !payoutSummary || !dateRange[0] || !dateRange[1]) return false;
    try {
      await axiosClient.post("/payroll/payout", {
        staff_id: selectedStaff.id,
        period_start: dateRange[0].format("YYYY-MM-DD"),
        period_end: dateRange[1].format("YYYY-MM-DD"),
        gross_pay: payoutSummary.totalPayout,
        deductions: 0,
        net_pay: payoutSummary.totalPayout,
        payment_method: method === "direct-deposit" ? "direct_deposit" : "check",
        notes: checkMemo,
      });
      message.success("Payout recorded");
      return true;
    } catch {
      message.error("Failed to record payout. Please try again.");
      return false;
    }
  }

  useEffect(() => {
    if (payrollStep === 3 && summaryRef.current) summaryRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    if (payrollStep === 4 && paymentRef.current) paymentRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [payrollStep]);

  function selectStaff(s: StaffPayrollInfo) {
    setSelectedStaff(s);
    setPayrollStep(2);
    setDateRange([null, null]);
    setPayoutSummary(null);
    setPaymentMethod(null);
    setShowSuccess(false);
  }

  async function generateSummary() {
    if (!selectedStaff || !dateRange[0] || !dateRange[1]) return;
    setSummaryLoading(true);
    try {
      const summary = await fetchPayoutData(selectedStaff, dateRange[0].format("YYYY-MM-DD"), dateRange[1].format("YYYY-MM-DD"));
      setPayoutSummary(summary);
      setPayrollStep(3);
    } catch {
      message.error("Failed to load payout summary");
    } finally {
      setSummaryLoading(false);
    }
  }

  async function openHistoryDetail(rec: PayoutRecord) {
    if (!selectedStaff) return;
    try {
      const summary = await fetchPayoutData(selectedStaff, rec.fromDateISO, rec.toDateISO);
      setHistoryDetail({ record: rec, summary });
      setShowHistoryAll(false);
    } catch {
      message.error("Failed to load payout detail");
    }
  }

  const checkNumber = salonBank.startingCheckNumber + (history.filter((r) => r.method === "check").length);

  function roleColor(role: string) {
    if (role === "Owner") return "purple";
    if (role === "Service Provider") return "blue";
    return "default";
  }

  return (
    <div className="admin-payroll-page">
      {/* Page header */}
      <div className="payroll-page-header">
        <div>
          <h1 className="payroll-page-title">Payroll</h1>
          <p className="payroll-page-sub">Manage staff payouts, direct deposits, and check writing</p>
        </div>
        <div className="payroll-tab-switcher">
          <button className={`payroll-tab-btn${activeTab === "payroll" ? " active" : ""}`} onClick={() => setActiveTab("payroll")}>Payroll</button>
          <button className={`payroll-tab-btn${activeTab === "settings" ? " active" : ""}`} onClick={() => setActiveTab("settings")}>Payment Settings</button>
        </div>
      </div>

      <div className="payroll-body">
        {/* ── PAYROLL TAB ── */}
        {activeTab === "payroll" && (
          <div className="payroll-split">
            {/* Roster */}
            <div className="payroll-roster">
              <div className="payroll-roster-header">
                <div style={{ fontWeight: 600, fontSize: 14, color: "#374151" }}>Staff Roster</div>
                <div style={{ fontSize: 12, color: "#9ca3af" }}>Select to run payroll</div>
              </div>
              <div className="payroll-roster-list">
                {staffList.map((s) => (
                  <button
                    key={s.id}
                    className={`payroll-roster-item${selectedStaff?.id === s.id ? " active" : ""}`}
                    onClick={() => selectStaff(s)}
                  >
                    <div className="payroll-avatar" style={{ background: avatarColor(s.id) }}>{s.avatar}</div>
                    <div style={{ minWidth: 0 }}>
                      <div className="payroll-roster-name">{s.name}</div>
                      <Tag color={roleColor(s.role)} style={{ fontSize: 11, marginTop: 2 }}>{s.role}</Tag>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Workflow */}
            <div className="payroll-workflow">
              {!selectedStaff ? (
                <div className="payroll-empty">
                  <div className="payroll-empty-icon"><User size={28} color="#3b82f6" /></div>
                  <div className="payroll-empty-title">Select a Staff Member</div>
                  <div className="payroll-empty-sub">Choose from the roster to begin payroll</div>
                </div>
              ) : (
                <div className="payroll-workflow-inner">
                  {/* Step indicator */}
                  <div className="payroll-steps">
                    {[{n:1,label:"Staff"},{n:2,label:"Dates"},{n:3,label:"Summary"},{n:4,label:"Payment"}].map((step, i) => (
                      <div key={step.n} style={{ display: "flex", alignItems: "center" }}>
                        <div className={`payroll-step-pill${payrollStep >= step.n ? " active" : ""}`}>
                          <span className="payroll-step-num">{payrollStep > step.n ? <Check size={12} strokeWidth={3} /> : step.n}</span>
                          <span>{step.label}</span>
                        </div>
                        {i < 3 && <ChevronRight size={14} color="#d1d5db" style={{ margin: "0 4px" }} />}
                      </div>
                    ))}
                  </div>

                  {/* Step 1: Staff card */}
                  <div className="payroll-step-card">
                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                      <div className="payroll-avatar payroll-avatar-lg" style={{ background: avatarColor(selectedStaff.id) }}>{selectedStaff.avatar}</div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 16 }}>{selectedStaff.name}</div>
                        <div style={{ fontSize: 13, color: "#6b7280" }}>{selectedStaff.role} · {payStructureLabel(selectedStaff)}</div>
                      </div>
                      <button className="payroll-change-btn" onClick={() => { setSelectedStaff(null); setPayrollStep(1); setPayoutSummary(null); setShowSuccess(false); }}>
                        Change Staff
                      </button>
                    </div>
                  </div>

                  {/* Step 2: Date range */}
                  {payrollStep >= 2 && (
                    <div className="payroll-step-card">
                      <div className="payroll-step-card-title"><CalendarIcon size={15} color="#1677ff" /> Select Pay Period</div>
                      <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
                        <div style={{ flex: 1, minWidth: 220 }}>
                          <RangePicker
                            style={{ width: "100%" }}
                            value={dateRange}
                            onChange={(vals) => {
                              setDateRange(vals as [Dayjs | null, Dayjs | null]);
                              setPayoutSummary(null);
                              setPayrollStep(2);
                              setShowSuccess(false);
                            }}
                          />
                        </div>
                        <Button
                          type="primary"
                          disabled={!dateRange[0] || !dateRange[1]}
                          loading={summaryLoading}
                          onClick={generateSummary}
                        >
                          Generate Summary
                        </Button>
                      </div>
                      {/* Quick presets */}
                      <div className="payroll-presets">
                        {[
                          { label: "Last 2 Weeks", days: 13 },
                          { label: "This Month", isMonth: true },
                          { label: "Last Month", isLastMonth: true },
                        ].map(({ label, days, isMonth, isLastMonth }) => (
                          <button
                            key={label}
                            className="payroll-preset-btn"
                            onClick={() => {
                              const today = dayjs();
                              let from: Dayjs, to: Dayjs = today;
                              if (isMonth) { from = today.startOf("month"); }
                              else if (isLastMonth) { to = today.subtract(1, "month").endOf("month"); from = to.startOf("month"); }
                              else { from = today.subtract(days!, "day"); }
                              setDateRange([from, to]);
                              setPayoutSummary(null);
                              setPayrollStep(2);
                            }}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Payout history mini */}
                  {payrollStep >= 2 && history.length > 0 && (
                    <div className="payroll-step-card" style={{ padding: 0, overflow: "hidden" }}>
                      <div className="payroll-hist-header">
                        <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, fontSize: 14 }}>
                          <History size={15} color="#1677ff" /> Payout History
                        </div>
                        <button className="payroll-hist-all-btn" onClick={() => setShowHistoryAll(true)}>View All</button>
                      </div>
                      <table className="payroll-hist-table">
                        <thead><tr><th>Processed</th><th>Pay Period</th><th>Method</th><th className="text-right">Amount</th></tr></thead>
                        <tbody>
                          {history.slice(0, 3).map((rec) => (
                            <tr key={rec.id} className="payroll-hist-row" onClick={() => openHistoryDetail(rec)}>
                              <td>{rec.processedDate}</td>
                              <td style={{ color: "#6b7280", fontSize: 12 }}>{rec.fromDate} – {rec.toDate}</td>
                              <td>
                                {rec.method === "direct-deposit"
                                  ? <Tag color="blue" icon={<Building2 size={10} />}> Direct Deposit</Tag>
                                  : <Tag color="gold" icon={<Printer size={10} />}> Check #{rec.checkNumber}</Tag>}
                              </td>
                              <td className="text-right" style={{ fontWeight: 600 }}>${rec.amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Step 3: Payout summary */}
                  {payrollStep >= 3 && payoutSummary && (
                    <div ref={summaryRef} className="payroll-step-card" style={{ padding: 0, overflow: "hidden" }}>
                      <div className="payroll-hist-header">
                        <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, fontSize: 14 }}>
                          <FileText size={15} color="#1677ff" /> Payout Summary
                        </div>
                        <span style={{ fontSize: 12, color: "#9ca3af" }}>{dateRange[0]?.format("MMM D")} – {dateRange[1]?.format("MMM D, YYYY")}</span>
                      </div>

                      <div className="payout-stat-row">
                        {[
                          { label: "Days Worked", value: payoutSummary.workDays.length },
                          { label: "Total Hours", value: payoutSummary.totalHours + "h" },
                          { label: "Services Gross", value: "$" + payoutSummary.totalServicesGross.toFixed(2) },
                          { label: "Products Gross", value: "$" + payoutSummary.totalProductsGross.toFixed(2) },
                        ].map((s) => (
                          <div key={s.label} className="payout-stat-cell">
                            <div className="payout-stat-label">{s.label}</div>
                            <div className="payout-stat-value">{s.value}</div>
                          </div>
                        ))}
                      </div>

                      <div style={{ padding: "0 24px 24px" }}>
                        {/* Work log (first 5) */}
                        <div className="payout-detail-section">
                          <div className="payout-detail-section-title"><Clock size={13} /> Work Log</div>
                          <table className="payroll-inner-table">
                            <thead><tr><th>Date</th><th>In</th><th>Out</th><th className="text-right">Hours</th></tr></thead>
                            <tbody>
                              {payoutSummary.workDays.slice(0, 5).map((d, i) => (
                                <tr key={i}><td>{d.dayName}, {d.date}</td><td>{d.timeIn}</td><td>{d.timeOut}</td><td className="text-right">{d.hoursWorked}h</td></tr>
                              ))}
                              {payoutSummary.workDays.length > 5 && <tr><td colSpan={4} className="more-row">+{payoutSummary.workDays.length - 5} more days</td></tr>}
                            </tbody>
                            <tfoot><tr><td colSpan={3} style={{ fontWeight: 600 }}>Total</td><td className="text-right" style={{ fontWeight: 700 }}>{payoutSummary.totalHours}h</td></tr></tfoot>
                          </table>
                        </div>

                        {/* Breakdown card */}
                        <div className="payout-breakdown-card">
                          <div className="payout-breakdown-label">Estimated Payout</div>
                          {payoutSummary.basePay > 0 && <div className="payout-breakdown-row"><span>Base Pay (prorated)</span><span>${payoutSummary.basePay.toFixed(2)}</span></div>}
                          {payoutSummary.serviceCommission > 0 && <div className="payout-breakdown-row"><span>Service Commission ({selectedStaff.serviceCommissionRate}%)</span><span>${payoutSummary.serviceCommission.toFixed(2)}</span></div>}
                          {payoutSummary.productCommission > 0 && <div className="payout-breakdown-row"><span>Product Commission ({selectedStaff.productCommissionRate}%)</span><span>${payoutSummary.productCommission.toFixed(2)}</span></div>}
                          <div className="payout-breakdown-total"><span>Total Payout</span><span>${payoutSummary.totalPayout.toFixed(2)}</span></div>
                        </div>

                        <Button type="primary" size="large" style={{ width: "100%", marginTop: 16 }} onClick={() => setPayrollStep(4)}>
                          Proceed to Payment →
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Step 4: Payment */}
                  {payrollStep >= 4 && payoutSummary && !showSuccess && (
                    <div ref={paymentRef} className="payroll-step-card">
                      <div className="payroll-step-card-title"><DollarSign size={15} color="#1677ff" /> Choose Payment Method</div>
                      <div className="payroll-payment-amount">
                        Total: <strong>${payoutSummary.totalPayout.toFixed(2)}</strong>
                      </div>
                      <div className="payroll-payment-methods">
                        {/* No bank-transfer integration yet — recording a "direct deposit" would claim money moved when it didn't. */}
                        <button className="payroll-pay-method-btn" disabled aria-disabled style={{ opacity: 0.5, cursor: "not-allowed" }}>
                          <Building2 size={22} color="#1677ff" />
                          <div className="payroll-pay-method-label">Direct Deposit <ComingSoonTag /></div>
                          <div className="payroll-pay-method-sub">{selectedStaff.bankInfo.bankName} ···{selectedStaff.bankInfo.accountNumber.slice(-4)}</div>
                        </button>
                        <button className="payroll-pay-method-btn" onClick={() => {
                          setCheckMemo(`Payroll: ${dateRange[0]?.format("MMM D")} – ${dateRange[1]?.format("MMM D, YYYY")}`);
                          setShowCheckWriter(true);
                        }}>
                          <Printer size={22} color="#f59e0b" />
                          <div className="payroll-pay-method-label">Print Check</div>
                          <div className="payroll-pay-method-sub">Check #{checkNumber}</div>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Success */}
                  {showSuccess && (
                    <div className="payroll-success-card">
                      <CheckCircle size={40} color="#16a34a" />
                      <div className="payroll-success-title">Payment Processed</div>
                      <div className="payroll-success-sub">
                        ${payoutSummary?.totalPayout.toFixed(2)} sent via {paymentMethod === "direct-deposit" ? `Direct Deposit to ${selectedStaff.bankInfo.bankName} ···${selectedStaff.bankInfo.accountNumber.slice(-4)}` : "Check"} to {selectedStaff.name}
                      </div>
                      {paymentMethod === "direct-deposit" && (
                        <div className="payroll-success-sub" style={{ fontSize: 12, color: "#9ca3af" }}>
                          Funds are typically deposited within 1–2 business days.
                        </div>
                      )}
                      <Button onClick={() => { setSelectedStaff(null); setPayrollStep(1); setPayoutSummary(null); setShowSuccess(false); }}>
                        Run Another Payroll
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── PAYMENT SETTINGS TAB ── */}
        {activeTab === "settings" && (
          <div className="payroll-settings-wrap">
            <div className="payroll-settings-inner-tabs">
              <button className={`payroll-inner-tab${settingsTab === "staff" ? " active" : ""}`} onClick={() => setSettingsTab("staff")}>Staff Banking</button>
              <button className={`payroll-inner-tab${settingsTab === "salon" ? " active" : ""}`} onClick={() => setSettingsTab("salon")}>Salon Banking</button>
            </div>

            {/* Staff banking */}
            {settingsTab === "staff" && (
              <div className="payroll-settings-card">
                <h3 className="payroll-settings-section-title">Staff Direct Deposit Info</h3>
                {staffList.map((s) => (
                  <div key={s.id} className="payroll-staff-bank-row">
                    <div className="payroll-avatar" style={{ background: avatarColor(s.id), flexShrink: 0 }}>{s.avatar}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{s.name}</div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>{s.bankInfo.bankName} · {s.bankInfo.accountType} {s.bankInfo.accountNumber}</div>
                    </div>
                    <Button size="small" onClick={() => setEditBankFor(s.id)}>Edit</Button>
                  </div>
                ))}
              </div>
            )}

            {/* Salon banking */}
            {settingsTab === "salon" && (
              <div className="payroll-settings-card">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                  <h3 className="payroll-settings-section-title" style={{ margin: 0 }}>Salon Bank Account</h3>
                  <Button onClick={() => { setSalonBankForm(salonBank); setEditSalonBank(true); }}>Edit</Button>
                </div>
                <div className="payroll-salon-bank-grid">
                  {[
                    { label: "Salon Name", value: salonBank.salonName },
                    { label: "Bank", value: salonBank.bankName },
                    { label: "Routing", value: salonBank.routingNumber },
                    { label: "Account", value: salonBank.accountNumber },
                    { label: "Address", value: `${salonBank.salonAddress}, ${salonBank.salonCity}, ${salonBank.salonState} ${salonBank.salonZip}` },
                    { label: "Next Check #", value: String(salonBank.startingCheckNumber) },
                  ].map((f) => (
                    <div key={f.label} className="payroll-salon-bank-field">
                      <div className="payroll-salon-bank-label">{f.label}</div>
                      <div className="payroll-salon-bank-value">{f.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Modals ── */}

      {showCheckWriter && payoutSummary && (
        <CheckWriterModal
          amount={payoutSummary.totalPayout}
          payToName={selectedStaff!.name}
          salonBank={salonBank}
          checkNumber={checkNumber}
          memo={checkMemo}
          onMemoChange={setCheckMemo}
          onClose={async () => {
            const ok = await recordPayout("check");
            setShowCheckWriter(false);
            if (!ok) return;
            setPaymentMethod("check");
            setShowSuccess(true);
          }}
        />
      )}

      {showDepositConfirm && (
        <Modal
          open
          title="Confirm Direct Deposit"
          onCancel={() => setShowDepositConfirm(false)}
          onOk={async () => {
            const ok = await recordPayout("direct-deposit");
            setShowDepositConfirm(false);
            if (!ok) return;
            setPaymentMethod("direct-deposit");
            setShowSuccess(true);
          }}
          okText="Confirm & Send"
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, background: "#f9fafb", borderRadius: 10, padding: 12, margin: "12px 0" }}>
            <div className="payroll-avatar" style={{ background: avatarColor(selectedStaff?.id ?? ""), flexShrink: 0 }}>{selectedStaff?.avatar}</div>
            <div>
              <div style={{ fontWeight: 600 }}>{selectedStaff?.name}</div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                {selectedStaff?.bankInfo.bankName} ···{selectedStaff?.bankInfo.accountNumber.slice(-4)}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderTop: "1px solid #f0f0f0" }}>
            <span style={{ color: "#6b7280" }}>Pay Period</span>
            <span>{dateRange[0]?.format("MMM D")} – {dateRange[1]?.format("MMM D, YYYY")}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderTop: "1px solid #f0f0f0", borderBottom: "1px solid #f0f0f0", marginBottom: 12 }}>
            <span style={{ color: "#6b7280" }}>Amount</span>
            <span style={{ fontWeight: 700, fontSize: 18 }}>${payoutSummary?.totalPayout.toFixed(2)}</span>
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Funds are typically deposited within 1–2 business days.
          </Text>
        </Modal>
      )}

      {showHistoryAll && selectedStaff && (
        <PayoutHistoryModal
          staff={selectedStaff}
          history={history}
          onClose={() => setShowHistoryAll(false)}
          onViewDetail={openHistoryDetail}
        />
      )}

      {historyDetail && (
        <PayoutDetailModal
          record={historyDetail.record}
          summary={historyDetail.summary}
          onClose={() => setHistoryDetail(null)}
        />
      )}

      {editBankFor && (
        <BankInfoModal
          staffName={staffList.find((s) => s.id === editBankFor)!.name}
          bankInfo={staffList.find((s) => s.id === editBankFor)!.bankInfo}
          onSave={async (info) => {
            try {
              await axiosClient.put("/payroll/bank-info", {
                staff_id: editBankFor,
                bank_name: info.bankName,
                routing_number: info.routingNumber,
                account_number: info.accountNumber,
                account_type: info.accountType,
              });
            } catch { /* local update still applies */ }
            setStaffList((prev) => prev.map((s) => (s.id === editBankFor ? { ...s, bankInfo: info } : s)));
            setEditBankFor(null);
            message.success("Bank info updated");
          }}
          onClose={() => setEditBankFor(null)}
        />
      )}

      {editSalonBank && (
        <Modal
          open
          title="Edit Salon Bank Account"
          onCancel={() => setEditSalonBank(false)}
          onOk={async () => {
            try {
              await axiosClient.put("/payroll/salon-bank", {
                bank_name: salonBankForm.bankName,
                routing_number: salonBankForm.routingNumber,
                account_number: salonBankForm.accountNumber,
                salon_name: salonBankForm.salonName,
                salon_address: salonBankForm.salonAddress,
                salon_city: salonBankForm.salonCity,
                salon_state: salonBankForm.salonState,
                salon_zip: salonBankForm.salonZip,
                starting_check_number: salonBankForm.startingCheckNumber,
              });
              setSalonBank(salonBankForm);
              setEditSalonBank(false);
              message.success("Salon bank updated");
            } catch {
              message.error("Failed to save salon bank info");
            }
          }}
          okText="Save"
          width={480}
        >
          <Form layout="vertical" style={{ marginTop: 16 }}>
            {[
              { key: "salonName", label: "Salon Name" },
              { key: "bankName", label: "Bank Name" },
              { key: "salonAddress", label: "Street Address" },
            ].map(({ key, label }) => (
              <Form.Item key={key} label={label}>
                <Input value={(salonBankForm as any)[key]} onChange={(e) => setSalonBankForm((p) => ({ ...p, [key]: e.target.value }))} />
              </Form.Item>
            ))}
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12 }}>
              <Form.Item label="City">
                <Input value={salonBankForm.salonCity} onChange={(e) => setSalonBankForm((p) => ({ ...p, salonCity: e.target.value }))} />
              </Form.Item>
              <Form.Item label="State">
                <Input value={salonBankForm.salonState} onChange={(e) => setSalonBankForm((p) => ({ ...p, salonState: e.target.value }))} maxLength={2} />
              </Form.Item>
              <Form.Item label="ZIP">
                <Input value={salonBankForm.salonZip} onChange={(e) => setSalonBankForm((p) => ({ ...p, salonZip: e.target.value }))} maxLength={10} />
              </Form.Item>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Form.Item label="Routing Number">
                <Input style={{ fontFamily: "monospace" }} value={salonBankForm.routingNumber} onChange={(e) => setSalonBankForm((p) => ({ ...p, routingNumber: e.target.value }))} maxLength={9} />
              </Form.Item>
              <Form.Item label="Account Number">
                <Input style={{ fontFamily: "monospace" }} value={salonBankForm.accountNumber} onChange={(e) => setSalonBankForm((p) => ({ ...p, accountNumber: e.target.value }))} />
              </Form.Item>
            </div>
            <Form.Item label="Starting Check Number">
              <Input
                type="number"
                value={salonBankForm.startingCheckNumber}
                onChange={(e) => setSalonBankForm((p) => ({ ...p, startingCheckNumber: Number(e.target.value) || 0 }))}
              />
            </Form.Item>
          </Form>
        </Modal>
      )}
    </div>
  );
}
